import { useMutation, useQuery, useQueryClient } from 'react-query';

import { customInstance } from './custom-instance';
import { handleApiQueryError } from '@ping/providers/ReactQueryProvider';

import type { UseMutationOptions, UseQueryOptions } from 'react-query';
import type { ErrorType } from './custom-instance';
import type { ApiErrorResponse } from './index';

export {
  CampaignStatus,
  campaignKeys,
  type CampaignConfig,
  type CampaignDetail,
  type CampaignLegalDocuments,
  type CampaignMyActivity,
  type CampaignMyParticipation,
  type CampaignMyResult,
  type CampaignParticipantActivityEvent,
  type CampaignParticipation,
  type CampaignPrize,
  type CampaignPrizeWinnerSummary,
  type CampaignSummary,
  type CampaignWinnersResponse,
  type JoinCampaignParams,
  type JoinCampaignResult,
} from './campaigns.types';

import type {
  CampaignConfig,
  CampaignDetail,
  CampaignMyActivity,
  CampaignMyParticipation,
  CampaignMyResult,
  CampaignSummary,
  CampaignWinnersResponse,
  JoinCampaignParams,
  JoinCampaignResult,
} from './campaigns.types';
import { campaignKeys } from './campaigns.types';

export const getCampaignBySlug = (slug: string) =>
  customInstance<CampaignDetail>({
    url: `/frontoffice/api/v1/campaigns/slug/${encodeURIComponent(slug)}`,
    method: 'GET',
  });

export const getCampaignWinners = (slug: string) =>
  customInstance<CampaignWinnersResponse>({
    url: `/frontoffice/api/v1/campaigns/slug/${encodeURIComponent(slug)}/winners`,
    method: 'GET',
  });

export const joinCampaign = ({
  campaignId,
  marketingChannel,
  cardPreference,
  acceptedLegalTerms,
}: JoinCampaignParams) =>
  customInstance<JoinCampaignResult>({
    url: `/frontoffice/api/v1/campaigns/${campaignId}/join`,
    method: 'POST',
    data: {
      ...(marketingChannel ? { marketingChannel } : {}),
      ...(cardPreference ? { cardPreference } : {}),
      ...(acceptedLegalTerms ? { acceptedLegalTerms: true } : {}),
    },
  });

export const getCampaignMyResult = (campaignId: string) =>
  customInstance<CampaignMyResult>({
    url: `/frontoffice/api/v1/campaigns/${campaignId}/my-result`,
    method: 'GET',
  });

export const getCampaignConfig = () =>
  customInstance<CampaignConfig>({
    url: '/frontoffice/api/v1/campaigns/config',
    method: 'GET',
  });

export const listCampaigns = () =>
  customInstance<CampaignSummary[]>({
    url: '/frontoffice/api/v1/campaigns',
    method: 'GET',
  });

export const listMyCampaignParticipations = () =>
  customInstance<CampaignMyParticipation[]>({
    url: '/frontoffice/api/v1/campaigns/mine',
    method: 'GET',
  });

export const getCampaignMyActivity = (campaignId: string) =>
  customInstance<CampaignMyActivity>({
    url: `/frontoffice/api/v1/campaigns/${campaignId}/my-activity`,
    method: 'GET',
  });

export function useCampaignBySlug(
  slug: string | undefined,
  options?: {
    query?: UseQueryOptions<CampaignDetail, ErrorType<ApiErrorResponse>>;
  }
) {
  const { onError: userOnError, ...restQuery } = options?.query ?? {};

  return useQuery({
    queryKey: campaignKeys.bySlug(slug ?? ''),
    queryFn: () => getCampaignBySlug(slug as string),
    enabled: Boolean(slug),
    ...restQuery,
    onError: (error, ...args) => {
      if (error.response?.status !== 404) {
        handleApiQueryError(error);
      }

      userOnError?.(error, ...args);
    },
  });
}

export function useCampaignWinners(
  slug: string | undefined,
  options?: {
    enabled?: boolean;
    query?: UseQueryOptions<CampaignWinnersResponse, ErrorType<ApiErrorResponse>>;
  }
) {
  const enabled = Boolean(slug) && (options?.enabled ?? true);

  return useQuery({
    queryKey: campaignKeys.winnersBySlug(slug ?? ''),
    queryFn: () => getCampaignWinners(slug as string),
    enabled,
    staleTime: 30_000,
    ...options?.query,
  });
}

export function useJoinCampaign(
  options?: UseMutationOptions<JoinCampaignResult, ErrorType<ApiErrorResponse>, JoinCampaignParams>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: joinCampaign,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(campaignKeys.all);
      queryClient.invalidateQueries(campaignKeys.myResult(variables.campaignId));
    },
    ...options,
  });
}

export function useCampaignMyResult(
  campaignId: string | undefined,
  enabled: boolean,
  options?: {
    query?: UseQueryOptions<CampaignMyResult, ErrorType<ApiErrorResponse>>;
  }
) {
  return useQuery({
    queryKey: campaignKeys.myResult(campaignId ?? ''),
    queryFn: () => getCampaignMyResult(campaignId as string),
    enabled: Boolean(campaignId) && enabled,
    ...options?.query,
  });
}

export function useCampaignConfig(options?: { query?: UseQueryOptions<CampaignConfig, ErrorType<ApiErrorResponse>> }) {
  return useQuery({
    queryKey: campaignKeys.config(),
    queryFn: getCampaignConfig,
    staleTime: 60_000,
    ...options?.query,
  });
}

export function useCampaignList(options?: { query?: UseQueryOptions<CampaignSummary[], ErrorType<ApiErrorResponse>> }) {
  return useQuery({
    queryKey: campaignKeys.list(),
    queryFn: listCampaigns,
    staleTime: 60_000,
    ...options?.query,
  });
}

export function useMyCampaigns(
  enabled: boolean,
  options?: { query?: UseQueryOptions<CampaignMyParticipation[], ErrorType<ApiErrorResponse>> }
) {
  return useQuery({
    queryKey: campaignKeys.myParticipations(),
    queryFn: listMyCampaignParticipations,
    enabled,
    staleTime: 30_000,
    ...options?.query,
  });
}

export function useCampaignMyActivity(
  campaignId: string | undefined,
  enabled: boolean,
  options?: { query?: UseQueryOptions<CampaignMyActivity, ErrorType<ApiErrorResponse>> }
) {
  return useQuery({
    queryKey: campaignKeys.myActivity(campaignId ?? ''),
    queryFn: () => getCampaignMyActivity(campaignId as string),
    enabled: Boolean(campaignId) && enabled,
    staleTime: 30_000,
    ...options?.query,
  });
}

export function useCompetitionsNavVisible() {
  // Nav links are always shown; campaigns config API is unchanged for other consumers.
  return {
    showCompetitionsNav: true,
    isLoading: false,
  };
}
